import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useSnackbar } from 'react-simple-snackbar'
import { fetchCorporateDashboard } from '../../api/index'
import { useCorporateGuard } from '../../hooks/useRoleGuard'
import Spinner from '../Spinner/Spinner'
import styles from '../Dashboard/Dashboard.module.css'

const MONTHS = [
  '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
]

const formatPeriod = (period) => {
  if (!period) return '—'
  const [year, month] = period.split('-')
  return `${MONTHS[Number(month)]} ${year}`
}

const CorporateDashboard = () => {
  const history = useHistory()
  const [openSnackbar] = useSnackbar()
  const openSnackbarRef = useRef(openSnackbar)
  openSnackbarRef.current = openSnackbar
  useCorporateGuard()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const page = 1
  const now = new Date()
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(now.getFullYear())

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data: result } = await fetchCorporateDashboard({
        page,
        limit: 5,
        month: selectedMonth,
        year: selectedYear,
      })
      setData(result)
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur chargement')
    } finally {
      setLoading(false)
    }
  }, [page, selectedMonth, selectedYear])

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedMonth, selectedYear])

  if (loading && !data) {
    return <div className={styles.loading}><Spinner /></div>
  }

  const { stats, recentCampaigns, selectedCampaign, selectedPeriod, availableYears = [] } = data || {}
  const emailStats = stats?.periodEmailStats || { sent: 0, pending: 0, failed: 0 }
  const emailTotal = emailStats.sent + emailStats.pending + emailStats.failed
  const emailProgress = emailTotal > 0 ? Math.round((emailStats.sent / emailTotal) * 100) : 0

  const currentMonth = now.getMonth() + 1
  const currentYear = now.getFullYear()
  const isCurrentPeriod = selectedMonth === currentMonth && selectedYear === currentYear
  const years = availableYears.length > 0 ? availableYears : [currentYear]

  const goToCurrentMonth = () => {
    setSelectedMonth(currentMonth)
    setSelectedYear(currentYear)
  }

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div>
          <h1>Dashboard Corporate</h1>
          <p>Conventions clients — envoi PDF Sage X3</p>
        </div>
        <button type="button" className={styles.ctaBtn} onClick={() => history.push('/corporate/import')}>
          + Nouvel envoi PDF
        </button>
      </div>

      <div className={styles.filterBar}>
        <div className={styles.filterGroup}>
          <label htmlFor="corp-dash-month">Mois</label>
          <select
            id="corp-dash-month"
            className={styles.filterSelect}
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
          >
            {MONTHS.slice(1).map((label, i) => (
              <option key={label} value={i + 1}>{label}</option>
            ))}
          </select>
        </div>
        <div className={styles.filterGroup}>
          <label htmlFor="corp-dash-year">Année</label>
          <select
            id="corp-dash-year"
            className={styles.filterSelect}
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        {!isCurrentPeriod && (
          <button type="button" className={styles.resetBtn} onClick={goToCurrentMonth}>
            Revenir au mois en cours
          </button>
        )}
      </div>

      <div className={styles.periodBanner}>
        <div>
          <span className={styles.periodLabel}>
            {isCurrentPeriod ? 'Période en cours' : 'Période sélectionnée'}
          </span>
          <strong>{formatPeriod(selectedPeriod || `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`)}</strong>
        </div>
        {selectedCampaign && (
          <div className={styles.periodStatus}>
            <span className={styles.badgeOk}>Campagne active</span>
            <span>{emailProgress}% emails envoyés</span>
            <button type="button" className={styles.linkBtn} onClick={() => history.push(`/corporate/campagnes/${selectedCampaign._id}`)}>
              Voir détail →
            </button>
          </div>
        )}
      </div>

      <div className={styles.kpiGrid}>
        <div className={`${styles.kpiCard} ${styles.kpiPrimary}`}>
          <span className={styles.kpiLabel}>Clients convention</span>
          <span className={styles.kpiValue}>{stats?.totalClients || 0}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>PDF période</span>
          <span className={styles.kpiValue}>{stats?.periodDocuments || 0}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Emails envoyés</span>
          <span className={styles.kpiValue}>{emailStats.sent} / {emailTotal || 0}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Campagnes totales</span>
          <span className={styles.kpiValue}>{stats?.totalCampaigns || 0}</span>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <h2>Campagnes récentes</h2>
        </div>
        {!recentCampaigns?.length ? (
          <p className={styles.empty}>Aucune campagne Corporate</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Période</th>
                <th>PDF</th>
                <th>Erreurs</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {recentCampaigns.map((c) => (
                <tr key={c.id}>
                  <td>{formatPeriod(c.period)}</td>
                  <td>{c.successCount}</td>
                  <td>{c.errorCount}</td>
                  <td>
                    <button type="button" className={styles.tableBtn} onClick={() => history.push(`/corporate/campagnes/${c.id}`)}>
                      Ouvrir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default CorporateDashboard
